import { Component, ComponentFactory, ComponentFactoryResolver, OnInit, ViewContainerRef, ViewChild, Input, inject } from '@angular/core';
import moment from 'moment';
import { OrderModel, RequestTemplateDescDTO, T22EntityDTO, T22EntityItemDTO } from 'src/app/store/order/model';
import { OrderFacade } from 'src/app/store/order/facade';
import { AttrDateComponent } from '../attr-date/attr-date.component';
import { AttrNumberComponent } from '../attr-number/attr-number.component';
import { AttrStringComponent } from '../attr-string/attr-string.component';
import { AttrUnknownComponent } from '../attr-unknown/attr-unknown.component';
import { AbstractOrderUIService } from 'src/app/core/services/abstract/abstract-order-ui-service';
import { AttrDictionaryComponent } from '../attr-dictionary/attr-dictionary.component';
import { AttrEntityComponent } from '../attr-entity/attr-entity.component';
import { AttrLogicalComponent } from '../attr-logical/attr-logical.component';
import { AttrPhoneComponent } from '../attr-phone/attr-phone.component';
import { AttrDateTimeComponent } from '../attr-date-time/attr-date-time.component';

@Component({
  selector: 'app-order-dynamic-attrs',
  templateUrl: './order-dynamic-attrs.component.html',
  styleUrls: ['./order-dynamic-attrs.component.scss'],
  standalone: false,
})
export class OrderDynamicAttrsComponent implements OnInit {
  private componentFactoryResolver = inject(ComponentFactoryResolver);
  private orderFacade = inject(OrderFacade);
  private orderUIService = inject(AbstractOrderUIService);

  @Input() order: OrderModel;

  @ViewChild('containerAttrs', { read: ViewContainerRef, static: true }) containerAttrs;

  isDisabled: boolean = false;
  properties: any[] = [];
  createdAttrs: boolean = false;
  resolverUnknown: ComponentFactory<AttrUnknownComponent>;
  resolverString: ComponentFactory<AttrStringComponent>;
  resolverNumber: ComponentFactory<AttrNumberComponent>;
  resolverDate: ComponentFactory<AttrDateComponent>;
  resolverDictionary: ComponentFactory<AttrDictionaryComponent>;
  resolverEntity: ComponentFactory<AttrEntityComponent>;
  resolverLogical: ComponentFactory<AttrLogicalComponent>;
  resolverPhone: ComponentFactory<AttrPhoneComponent>;
  resolverDateTime: ComponentFactory<AttrDateTimeComponent>;

  ngOnInit(): void {
    this.isDisabled = this.orderUIService.isDisabled(this.order);
    this.resolverUnknown = this.componentFactoryResolver.resolveComponentFactory(AttrUnknownComponent);
    this.resolverString = this.componentFactoryResolver.resolveComponentFactory(AttrStringComponent);
    this.resolverNumber = this.componentFactoryResolver.resolveComponentFactory(AttrNumberComponent);
    this.resolverDate = this.componentFactoryResolver.resolveComponentFactory(AttrDateComponent);
    this.resolverDictionary = this.componentFactoryResolver.resolveComponentFactory(AttrDictionaryComponent);
    this.resolverEntity = this.componentFactoryResolver.resolveComponentFactory(AttrEntityComponent);
    this.resolverLogical = this.componentFactoryResolver.resolveComponentFactory(AttrLogicalComponent);
    this.resolverPhone = this.componentFactoryResolver.resolveComponentFactory(AttrPhoneComponent);

    this.orderFacade.template().subscribe(template => {
      if (template && template.requestTemplateDescs) {
        this.createAttributes([...template.requestTemplateDescs]);
      }
    });
  }

  createAttributes(template: RequestTemplateDescDTO[]) {
    this.properties = [];
    this.containerAttrs.clear();

    //console.log("template", template);
    template
      .sort((a, b) => {
        return a.sortOrder - b.sortOrder;
      })
      .forEach(property => {
        if (property.property.type.id === 1) {
          this.createStringAttr(
            property.property.id,
            property.property.name,
            this.getPropertyValue(property.property.id),
            property.mandatory
          );
        } else if (property.property.type.id === 2) {
          this.createNumberAttr(
            property.property.id,
            property.property.name,
            this.getPropertyValue(property.property.id),
            property.mandatory
          );
        } else if (property.property.type.id === 13) {
          this.createDateAttr(
            property.property.id,
            property.property.name,
            this.getPropertyValue(property.property.id),
            property.mandatory
          );
        } else if (property.property.type.id === 28) {
          this.createDateTimeAttr(
            property.property.id,
            property.property.name,
            this.getPropertyValue(property.property.id),
            property.mandatory
          );
        } else if (property.property.type.id === 5) {
          this.createLogicalAttr(
            property.property.id,
            property.property.name,
            this.getPropertyValue(property.property.id),
            property.mandatory
          );
        } else if (property.property.type.id === 14) {
          this.createPhoneAttr(
            property.property.id,
            property.property.name,
            this.getPropertyValue(property.property.id),
            property.mandatory
          );
        } else if (property.property.type.id === 25) {
          this.createDictionaryValueKeyAttr(
            property.property.id,
            property.property.dictionary.id,
            property.property.name,
            this.getPropertyValue(property.property.id),
            property.mandatory
          );
        } else if (property.property.dictionary && !property.property.dictionary.entity && property.property.dictionary.id) {
          this.createDictionaryAttr(
            property.property.id,
            property.property.dictionary.id,
            property.property.name,
            this.getPropertyValue(property.property.id),
            property.mandatory
          );
        } else if (property.property.dictionary && property.property.dictionary.entity && property.property.dictionary.entity.id) {
          this.createEntityAttr(
            property.property.id,
            property.property.dictionary.entity,
            property.property.name,
            this.getPropertyValue(property.property.id),
            this.getEntityPropertyValue(property.property.id),
            property.mandatory
          );
        } else {
          //console.log("property", property);
          this.createUnknownAttr(property.property.type.value + ': ' + property.property.name, property.mandatory);
        }
      });

    this.createdAttrs = true;
  }

  getPropertyValue(propertyId: number): string {
    if (!this.order || !this.order.properties) {
      return null;
    }

    var res = this.order.properties.find(x => x.property.id == propertyId);
    return res ? res.value : null;
  }

  getEntityPropertyValue(propertyId: number): T22EntityItemDTO {
    if (!this.order || !this.order.properties) {
      return null;
    }

    var res = this.order.properties.find(x => x.property.id == propertyId);
    return res ? res.entityValue : null;
  }
  createUnknownAttr(caption: string, mandatory: boolean = null) {
    let componentFactory = this.containerAttrs.createComponent(this.resolverUnknown);
    componentFactory.instance.caption = caption;
    componentFactory.instance.mandatory = mandatory;
  }

  createStringAttr(id: number, caption: string, value: string, mandatory: boolean = null) {
    let componentFactory = this.containerAttrs.createComponent(this.resolverString);
    componentFactory.instance.propertyId = id;
    componentFactory.instance.caption = caption;
    componentFactory.instance.value = value;
    componentFactory.instance.mandatory = mandatory;
    componentFactory.instance.isDisabled = this.isDisabled;
    this.properties.push(componentFactory.instance);
  }

  createNumberAttr(id: number, caption: string, value: string, mandatory: boolean = null) {
    let componentFactory = this.containerAttrs.createComponent(this.resolverNumber);
    componentFactory.instance.propertyId = id;
    componentFactory.instance.caption = caption;
    componentFactory.instance.value = value;
    componentFactory.instance.mandatory = mandatory;
    componentFactory.instance.isDisabled = this.isDisabled;
    this.properties.push(componentFactory.instance);
  }

  createDateAttr(id: number, caption: string, value: string, mandatory: boolean = null) {
    let componentFactory = this.containerAttrs.createComponent(this.resolverDate);
    componentFactory.instance.propertyId = id;
    componentFactory.instance.caption = caption;
    componentFactory.instance.value = value ? moment(value, 'D/MM/yyyy').toDate() : null;
    componentFactory.instance.mandatory = mandatory;
    componentFactory.instance.isDisabled = this.isDisabled;
    this.properties.push(componentFactory.instance);
  }

  createDateTimeAttr(id: number, caption: string, value: string, mandatory: boolean = null) {
    let componentFactory = this.containerAttrs.createComponent(this.resolverDateTime);
    componentFactory.instance.propertyId = id;
    componentFactory.instance.caption = caption;
    componentFactory.instance.value = value ? moment(value, 'D/MM/yyyy HH:mm:ss').toDate() : null;
    componentFactory.instance.mandatory = mandatory;
    componentFactory.instance.isDisabled = this.isDisabled;
    this.properties.push(componentFactory.instance);
  }

  createDictionaryAttr(id: number, dictionaryId: number, caption: string, value: string, mandatory: boolean = null) {
    let componentFactory = this.containerAttrs.createComponent(this.resolverDictionary);
    componentFactory.instance.propertyId = id;
    componentFactory.instance.dictionaryId = dictionaryId;
    componentFactory.instance.caption = caption;
    componentFactory.instance.value = value;
    componentFactory.instance.mandatory = mandatory;
    componentFactory.instance.isDisabled = this.isDisabled;
    componentFactory.instance.valueKey = false;
    this.properties.push(componentFactory.instance);
  }

  createDictionaryValueKeyAttr(id: number, dictionaryId: number, caption: string, value: string, mandatory: boolean = null) {
    let componentFactory = this.containerAttrs.createComponent(this.resolverDictionary);
    componentFactory.instance.propertyId = id;
    componentFactory.instance.dictionaryId = dictionaryId;
    componentFactory.instance.caption = caption;
    componentFactory.instance.value = value;
    componentFactory.instance.mandatory = mandatory;
    componentFactory.instance.isDisabled = this.isDisabled;
    componentFactory.instance.valueKey = true;
    this.properties.push(componentFactory.instance);
  }

  createEntityAttr(
    id: number,
    entity: T22EntityDTO,
    caption: string,
    value: string,
    entityValue: T22EntityItemDTO,
    mandatory: boolean = null
  ) {
    let componentFactory = this.containerAttrs.createComponent(this.resolverEntity);
    componentFactory.instance.propertyId = id;
    componentFactory.instance.entity = entity;
    componentFactory.instance.caption = caption;
    componentFactory.instance.value = value;
    componentFactory.instance.entityValue = entityValue;
    componentFactory.instance.mandatory = mandatory;
    componentFactory.instance.isDisabled = this.isDisabled;
    this.properties.push(componentFactory.instance);
  }

  createLogicalAttr(id: number, caption: string, value: string, mandatory: boolean = null) {
    let componentFactory = this.containerAttrs.createComponent(this.resolverLogical);
    componentFactory.instance.propertyId = id;
    componentFactory.instance.caption = caption;
    componentFactory.instance.value = value;
    componentFactory.instance.mandatory = mandatory;
    componentFactory.instance.isDisabled = this.isDisabled;
    this.properties.push(componentFactory.instance);
  }

  createPhoneAttr(id: number, caption: string, value: string, mandatory: boolean = null) {
    let componentFactory = this.containerAttrs.createComponent(this.resolverPhone);
    componentFactory.instance.propertyId = id;
    componentFactory.instance.caption = caption;
    componentFactory.instance.value = value;
    componentFactory.instance.mandatory = mandatory;
    componentFactory.instance.isDisabled = this.isDisabled;
    this.properties.push(componentFactory.instance);
  }
}
